/**
 * The hero's entrance: title, copy, chips, call to action arrive one after
 * another on first paint (life-layer.md §2, landing row; §3 P4).
 *
 * Owns: the four `from` tweens and how to stop them. Must not: leave anything
 * hidden in CSS. The resting state is the visible one; each element goes to
 * "hidden" only inside the gsap call, so if script fails the page is simply
 * already in place. Marked elements are `data-hero="title|chips|copy|cta"`.
 * Under reduced motion `enter` plays nothing at all.
 */
import { enter, gsap } from '@/ui/motion/gsap';

export type HeroPart = 'chips' | 'title' | 'copy' | 'cta';

interface Step {
  from: gsap.TweenVars;
  /** Seconds after first paint; 0 / 120 / 240 / 360 ms is the spec's cadence. */
  delay: number;
}

/** Values are the spec's (life-layer.md §2): 0.45 s power3.out; y 24 over 0.55 s; back.out(1.4) pop. */
export const HERO_STEPS: Readonly<Record<HeroPart, Step>> = {
  title: { from: { scale: 1.3, rotation: -3, opacity: 0, duration: 0.6, ease: 'back.out(1.8)' }, delay: 0 },
  chips: { from: { y: 24, opacity: 0, duration: 0.55, ease: 'power3.out' }, delay: 0.12 },
  copy: { from: { y: 24, opacity: 0, duration: 0.55, ease: 'power3.out' }, delay: 0.24 },
  cta: { from: { scale: 0.82, opacity: 0, duration: 0.45, ease: 'back.out(1.4)' }, delay: 0.36 },
};

/** Plays the entrance for the marked children of `root`; returns a stop that kills what is still running. */
export function playHeroEntrance(root: HTMLElement): () => void {
  const targets: Element[] = [];
  for (const part of Object.keys(HERO_STEPS) as HeroPart[]) {
    const el = root.querySelector(`[data-hero="${part}"]`);
    if (!el) continue;
    targets.push(el);
    enter(el, { ...HERO_STEPS[part].from, delay: HERO_STEPS[part].delay });
  }
  return () => gsap.killTweensOf(targets);
}
